// 画面固有の派生項目(原価率・サブブランド・ブランド品番・固定採番)の生成と差し込みをまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createDerivedFieldService(deps) {
    const {
        BRAND_GROUP_COLUMNID,
        BRAND_HINBAN_COLUMNID,
        BRAND_HINBAN_LABEL,
        BRAND_HINBAN_PREREQUISITE_KEYS,
        BRAND_HINBAN_PREREQUISITE_LENGTHS,
        BRAND_HINBAN_SERIAL_DATAID,
        BRAND_HINBAN_SERIAL_LABEL,
        CELLTYPE,
        COST_RATE_COLUMNID,
        COST_RATE_INSERT_POSITION,
        COST_RATE_LABEL,
        COST_RATE_TARGET_TYPE,
        DeepCopy,
        DicVal,
        FIXED_HINBAN_NUMBERING_LABEL,
        FormatDate,
        G_id,
        SUB_BRAND_COLUMNID,
        SUB_BRAND_FALLBACK_COLUMNID,
        SUB_BRAND_LABEL,
        createFixedHinbanNumberingField,
        dispatch,
        entrydata,
        entrydataRef,
        entryrerender,
        findBrandHinbanSerialField,
        formstatus,
        getEntryDataValue,
        isEmpty,
        isFixedHinbanNumberingTarget,
        matchesColumnId,
        normalizeNumberText,
        rerender,
        setEntryDataValue
    } = deps;

    function calculateCostRateValue(price, plannedCost) {
        const normalizedPrice = normalizeNumberText(price);
        const normalizedPlannedCost = normalizeNumberText(plannedCost);
        if (isEmpty(normalizedPrice) || isEmpty(normalizedPlannedCost)) return "";
        const priceNumber = Number(normalizedPrice);
        const plannedCostNumber = Number(normalizedPlannedCost);
        if (!Number.isFinite(priceNumber) || !Number.isFinite(plannedCostNumber) || plannedCostNumber === 0) return "";
        return String(priceNumber / plannedCostNumber);
    }
    function createCostRateField() {
        return {
            columnid: COST_RATE_COLUMNID,
            columnnm: COST_RATE_LABEL,
            table: "",
            logpid: G_id.BASE,
            type: COST_RATE_TARGET_TYPE,
            datatype: CELLTYPE.NUM,
            options: [],
            width: 0,
            hidden: "2",
            length: 0,
            format: "",
            decimalplace: 4,
            dbcolumn: "",
            dataid: "",
            initvalue: "",
            trigger: [],
            required: false,
            locked: true,
            unchange: false,
            primary: false,
            multiple: false,
            history: false,
            __clientOnly: true
        };
    }
    function createSubBrandField() {
        return {
            columnid: SUB_BRAND_COLUMNID,
            columnnm: SUB_BRAND_LABEL,
            table: "",
            logpid: G_id.BASE,
            type: "100",
            datatype: CELLTYPE.SELECT,
            options: [],
            width: 0,
            hidden: "2",
            length: 5,
            format: "",
            decimalplace: 0,
            dbcolumn: "",
            dataid: "",
            initvalue: "",
            trigger: [],
            required: true,
            locked: false,
            unchange: false,
            primary: false,
            multiple: false,
            history: false
        };
    }
    function normalizeSubBrandField(field) {
        const subBrandField = DeepCopy(isEmpty(field) ? createSubBrandField() : field);
        subBrandField.columnid = SUB_BRAND_COLUMNID;
        subBrandField.columnnm = SUB_BRAND_LABEL;
        subBrandField.table = DicVal(subBrandField, "table", "");
        subBrandField.logpid = G_id.BASE;
        subBrandField.type = DicVal(subBrandField, "type", "100");
        subBrandField.datatype = DicVal(subBrandField, "datatype", CELLTYPE.SELECT);
        if (isEmpty(subBrandField.options)) subBrandField.options = [];
        subBrandField.hidden = "2";
        if (isEmpty(subBrandField.length)) subBrandField.length = 5;
        if (isEmpty(subBrandField.format)) subBrandField.format = "";
        if (isEmpty(subBrandField.decimalplace)) subBrandField.decimalplace = 0;
        if (isEmpty(subBrandField.dbcolumn)) subBrandField.dbcolumn = "";
        if (isEmpty(subBrandField.dataid)) subBrandField.dataid = "";
        if (isEmpty(subBrandField.initvalue)) subBrandField.initvalue = "";
        if (isEmpty(subBrandField.trigger)) subBrandField.trigger = [];
        subBrandField.required = true;
        subBrandField.locked = false;
        subBrandField.unchange = false;
        subBrandField.primary = false;
        subBrandField.multiple = !!subBrandField.multiple;
        subBrandField.history = !!subBrandField.history;
        delete subBrandField.editableOnNewFormOnly;
        return subBrandField;
    }
    function resolveSubBrandField(basefields) {
        if (isEmpty(basefields)) return createSubBrandField();
        const exact = basefields.find(e => matchesColumnId(DicVal(e, "columnid", ""), SUB_BRAND_COLUMNID));
        if (!isEmpty(exact)) return normalizeSubBrandField(exact);
        const fallback = basefields.find(e => matchesColumnId(DicVal(e, "columnid", ""), SUB_BRAND_FALLBACK_COLUMNID));
        if (isEmpty(fallback)) return createSubBrandField();
        return normalizeSubBrandField({
            ...fallback,
            columnid: SUB_BRAND_COLUMNID,
            columnnm: SUB_BRAND_LABEL,
            dbcolumn: "",
            dataid: "",
            initvalue: "",
            trigger: []
        });
    }
    function insertSubBrandField(fields, subBrandField) {
        if (isEmpty(fields)) return isEmpty(subBrandField) ? [] : [normalizeSubBrandField(subBrandField)];
        const normalizedSubBrand = normalizeSubBrandField(subBrandField);
        const newfields = fields
            .filter(e => !matchesColumnId(DicVal(e, "columnid", ""), SUB_BRAND_COLUMNID))
            .map(e => DeepCopy(e));
        const brandGroupIndex = newfields.findIndex(e => matchesColumnId(DicVal(e, "columnid", ""), BRAND_GROUP_COLUMNID));
        const insertIndex = brandGroupIndex === -1 ? newfields.length : brandGroupIndex;
        newfields.splice(insertIndex, 0, normalizedSubBrand);
        return newfields;
    }
    function normalizeCostRateField(field) {
        const costRateField = DeepCopy(isEmpty(field) ? createCostRateField() : field);
        costRateField.columnid = COST_RATE_COLUMNID;
        costRateField.columnnm = COST_RATE_LABEL;
        costRateField.table = DicVal(costRateField, "table", "");
        costRateField.logpid = G_id.BASE;
        costRateField.type = COST_RATE_TARGET_TYPE;
        costRateField.datatype = CELLTYPE.NUM;
        costRateField.options = [];
        costRateField.hidden = "2";
        if (isEmpty(costRateField.length)) costRateField.length = 0;
        if (isEmpty(costRateField.format)) costRateField.format = "";
        if (isEmpty(costRateField.decimalplace)) costRateField.decimalplace = 4;
        if (isEmpty(costRateField.dbcolumn)) costRateField.dbcolumn = "";
        if (isEmpty(costRateField.dataid)) costRateField.dataid = "";
        if (isEmpty(costRateField.initvalue)) costRateField.initvalue = "";
        if (isEmpty(costRateField.trigger)) costRateField.trigger = [];
        costRateField.required = !!costRateField.required;
        costRateField.locked = true;
        costRateField.unchange = !!costRateField.unchange;
        costRateField.primary = !!costRateField.primary;
        costRateField.multiple = !!costRateField.multiple;
        costRateField.history = !!costRateField.history;
        return costRateField;
    }
    function resolveCostRateField(basefields) {
        if (isEmpty(basefields)) return createCostRateField();
        const exact = basefields.find(e => String(DicVal(e, "type", "")) === COST_RATE_TARGET_TYPE && e.columnid === COST_RATE_COLUMNID);
        if (!isEmpty(exact)) return normalizeCostRateField(exact);
        return createCostRateField();
    }
    function insertCostRateField(fields, costRateField) {
        if (isEmpty(fields)) return isEmpty(costRateField) ? [] : [costRateField];
        const normalizedCostRate = normalizeCostRateField(costRateField);
        const newfields = fields
            .filter(e => !(String(DicVal(e, "type", "")) === COST_RATE_TARGET_TYPE && (e.columnid === normalizedCostRate.columnid || e.columnnm === COST_RATE_LABEL)))
            .map(e => DeepCopy(e));
        const targetIndexes = [];
        for (let i = 0; i < newfields.length; i++) {
            if (String(DicVal(newfields[i], "type", "")) === COST_RATE_TARGET_TYPE) targetIndexes.push(i);
        }
        if (targetIndexes.length === 0) {
            newfields.push(normalizedCostRate);
            return newfields;
        }
        const insertIndex = targetIndexes.length > COST_RATE_INSERT_POSITION
            ? targetIndexes[COST_RATE_INSERT_POSITION]
            : targetIndexes[targetIndexes.length - 1] + 1;
        newfields.splice(insertIndex, 0, normalizedCostRate);
        return newfields;
    }
    function applyCostRateToEntryData(data) {
        if (isEmpty(data)) return false;
        const newValue = calculateCostRateValue(DicVal(data, "PDT_PRICE", ""), DicVal(data, "PDTMD_YOTEIGENKA", ""));
        const oldValue = DicVal(data, COST_RATE_COLUMNID, "");
        if (String(oldValue) === String(newValue)) return false;
        data[COST_RATE_COLUMNID] = newValue;
        return true;
    }
    function createBrandHinbanField() {
        return {
            columnid: BRAND_HINBAN_COLUMNID,
            columnnm: BRAND_HINBAN_LABEL,
            table: "",
            logpid: G_id.BASE,
            type: "100",
            datatype: CELLTYPE.TEXT,
            options: [],
            width: 1,
            hidden: "2",
            length: 10,
            format: "",
            decimalplace: 0,
            dbcolumn: "",
            dataid: "",
            initvalue: "",
            trigger: [],
            required: true,
            locked: false,
            unchange: false,
            primary: false,
            multiple: false,
            history: false,
            editableOnNewFormOnly: true
        };
    }
    function normalizeBrandHinbanField(field) {
        const brandField = DeepCopy(isEmpty(field) ? createBrandHinbanField() : field);
        brandField.columnid = BRAND_HINBAN_COLUMNID;
        brandField.columnnm = BRAND_HINBAN_LABEL;
        brandField.table = "M_PDT";
        brandField.saveed = true;
        brandField.logpid = G_id.BASE;
        brandField.type = "100";
        brandField.datatype = CELLTYPE.TEXT;
        if (isEmpty(brandField.options)) brandField.options = [];
        brandField.hidden = "2";
        if (isEmpty(brandField.length)) brandField.length = 10;
        if (isEmpty(brandField.format)) brandField.format = "";
        if (isEmpty(brandField.decimalplace)) brandField.decimalplace = 0;
        if (isEmpty(brandField.dbcolumn)) brandField.dbcolumn = "";
        if (isEmpty(brandField.dataid)) brandField.dataid = "";
        if (isEmpty(brandField.initvalue)) brandField.initvalue = "";
        if (isEmpty(brandField.trigger)) brandField.trigger = [];
        brandField.required = true;
        brandField.locked = false;
        brandField.unchange = false;
        brandField.primary = false;
        brandField.multiple = !!brandField.multiple;
        brandField.history = !!brandField.history;
        brandField.editableOnNewFormOnly = true;
        brandField.history = true;
        return brandField;
    }
    function resolveBrandHinbanField(basefields) {
        if (isEmpty(basefields)) return createBrandHinbanField();
        const exact = basefields.find(e => matchesColumnId(DicVal(e, "columnid", ""), BRAND_HINBAN_COLUMNID));
        if (!isEmpty(exact)) return normalizeBrandHinbanField(exact);
        const makerField = basefields.find(e => matchesColumnId(DicVal(e, "columnid", ""), "PDT_MAKERHINBAN"));
        if (isEmpty(makerField)) return createBrandHinbanField();
        return normalizeBrandHinbanField({
            ...makerField,
            columnid: BRAND_HINBAN_COLUMNID,
            columnnm: BRAND_HINBAN_LABEL,
            dbcolumn: "",
            dataid: "",
            initvalue: "",
            trigger: []
        });
    }
    function insertBrandHinbanField(fields, brandHinbanField) {
        if (isEmpty(fields)) return isEmpty(brandHinbanField) ? [] : [normalizeBrandHinbanField(brandHinbanField)];
        const normalizedBrandHinban = normalizeBrandHinbanField(brandHinbanField);
        const newfields = fields
            .filter(e => !(String(DicVal(e, "type", "")) === "100" && matchesColumnId(DicVal(e, "columnid", ""), BRAND_HINBAN_COLUMNID)))
            .map(e => DeepCopy(e));
        const makerIndex = newfields.findIndex(e => matchesColumnId(DicVal(e, "columnid", ""), "PDT_MAKERHINBAN"));
        const priceIndex = newfields.findIndex(e => matchesColumnId(DicVal(e, "columnid", ""), "PDT_PRICE"));
        let insertIndex = priceIndex;
        if (insertIndex === -1) {
            insertIndex = makerIndex === -1 ? newfields.length : makerIndex + 1;
        } else if (makerIndex !== -1 && makerIndex + 1 > insertIndex) {
            insertIndex = makerIndex + 1;
        }
        newfields.splice(insertIndex, 0, normalizedBrandHinban);
        return newfields;
    }
    function buildHinbanHeadlineColumnInfo(fields) {
        if (isEmpty(fields)) return fields;
        const brandHinbanField = normalizeBrandHinbanField(
            fields.find(e => matchesColumnId(DicVal(e, "columnid", ""), BRAND_HINBAN_COLUMNID))
                || resolveBrandHinbanField(fields)
        );
        const headlineBrandHinbanField = {
            ...brandHinbanField,
            required: true,
            headerfix: 1
        };
        const newfields = fields
            .filter(e => !matchesColumnId(DicVal(e, "columnid", ""), BRAND_HINBAN_COLUMNID))
            .map(e => DeepCopy(e));
        const brandIndex = newfields.findIndex(e => matchesColumnId(DicVal(e, "columnid", ""), "PDT_BRDCODE"));
        const hinmeiIndex = newfields.findIndex(e => matchesColumnId(DicVal(e, "columnid", ""), "PDT_HINMEINM"));
        let insertIndex = brandIndex === -1 ? hinmeiIndex : brandIndex + 1;
        if (insertIndex === -1) insertIndex = newfields.length;
        newfields.splice(insertIndex, 0, headlineBrandHinbanField);
        return newfields;
    }
    function formatHinbanHeadlineValue(field, sourceData) {
        const columnId = DicVal(field, "columnid", "");
        if (isEmpty(sourceData)) return "";
        if (formstatus === "ENTRY FORM" && !isEmpty(DicVal(field, "displayValueMap", null))) {
            const sourceColumnId = DicVal(field, "displayValueSourceColumn", columnId);
            const sourceValue = DicVal(sourceData, sourceColumnId, "");
            return DicVal(field.displayValueMap, String(isEmpty(sourceValue) ? "" : sourceValue), DicVal(field, "displayDefaultValue", ""));
        }
        if (field.datatype === CELLTYPE.DATE) return FormatDate(DicVal(sourceData, columnId, ""));
        let value = DicVal(sourceData, columnId, "");
        if (field.datatype === CELLTYPE.MASTER && !isEmpty(value)) {
            const name = DicVal(sourceData, columnId + "_nm", "");
            value = name !== "" ? value + ":" + name : "";
        }
        return value;
    }
    function resolveBrandHinbanPrefix(targetData) {
        if (isEmpty(targetData)) return "";
        let prefix = "";
        for (let i = 0; i < BRAND_HINBAN_PREREQUISITE_KEYS.length; i++) {
            const rawValue = String(getEntryDataValue(targetData, BRAND_HINBAN_PREREQUISITE_KEYS[i], "")).trim();
            if (isEmpty(rawValue)) return "";
            const length = BRAND_HINBAN_PREREQUISITE_LENGTHS[i];
            const normalizedValue = rawValue.padStart(length, '0');
            prefix += normalizedValue.substring(normalizedValue.length - length);
        }
        return prefix;
    }
    function normalizeBrandHinbanSerialValue(value) {
        const text = String(isEmpty(value) ? "" : value).trim();
        if (isEmpty(text)) return "";
        if (!/^\d+$/.test(text)) return text;
        return text.padStart(3, '0').slice(-3);
    }
    function buildBrandHinbanCode(prefix, serialValue) {
        const normalizedPrefix = String(isEmpty(prefix) ? "" : prefix).trim();
        const normalizedSerial = normalizeBrandHinbanSerialValue(serialValue);
        if (isEmpty(normalizedPrefix) || isEmpty(normalizedSerial)) return "";
        return normalizedPrefix + normalizedSerial;
    }
    function createBrandHinbanSerialSearchField(field, selectMode) {
        const serialField = findBrandHinbanSerialField();
        const sourceField = isEmpty(serialField) ? field : serialField;
        const searchField = DeepCopy(isEmpty(sourceField) ? {} : sourceField);
        searchField.columnid = BRAND_HINBAN_COLUMNID;
        searchField.columnnm = BRAND_HINBAN_SERIAL_LABEL;
        searchField.logpid = G_id.BASE;
        searchField.type = "100";
        searchField.dataid = BRAND_HINBAN_SERIAL_DATAID;
        searchField.datatype = CELLTYPE.TEXT;
        searchField.history = true;
        searchField.multiple = false;
        searchField.locked = false;
        searchField.unchange = false;
        searchField.primary = false;
        searchField.__brandHinbanSerialSelectMode = selectMode;
        return searchField;
    }
    function handleBrandHinbanSerialSelection(id, value, options) {
        if (String(DicVal(options, "source", "")).trim() !== "searchlist") return false;
        if (!matchesColumnId(id, BRAND_HINBAN_COLUMNID)) return false;

        const serialValue = normalizeBrandHinbanSerialValue(value);
        if (isEmpty(serialValue)) return false;
        return applyBrandHinbanCodeToEntryData(entrydataRef.current, serialValue);
    }
    function hasBrandHinbanPrerequisites(targetData) {
        if (formstatus !== "NEW FORM" || isEmpty(targetData)) return false;
        return BRAND_HINBAN_PREREQUISITE_KEYS.every((key) => !isEmpty(String(getEntryDataValue(targetData, key, "")).trim()));
    }
    function applyBrandHinbanCodeToEntryData(targetData, serialValue) {
        const prefix = resolveBrandHinbanPrefix(targetData);
        const brandHinbanCode = buildBrandHinbanCode(prefix, serialValue);
        if (isEmpty(brandHinbanCode)) return false;
        let changed = false;
        if (String(DicVal(targetData, BRAND_HINBAN_COLUMNID, "")) !== brandHinbanCode) {
            targetData[BRAND_HINBAN_COLUMNID] = brandHinbanCode;
            changed = true;
        }
        const serialField = findBrandHinbanSerialField();
        const serialColumnId = String(DicVal(serialField, "columnid", "")).trim();
        const normalizedSerial = normalizeBrandHinbanSerialValue(serialValue);
        if (!isEmpty(serialColumnId) && !isEmpty(normalizedSerial)) {
            changed = setEntryDataValue(targetData, serialColumnId, normalizedSerial) || changed;
        }
        if (changed) {
            dispatch(entrydata({ ...targetData }));
            dispatch(entryrerender());
            rerender();
        }
        return changed;
    }
    function normalizeFixedHinbanNumberingField(field) {
        const fixedField = DeepCopy(isEmpty(field) ? createFixedHinbanNumberingField() : field);
        fixedField.columnnm = FIXED_HINBAN_NUMBERING_LABEL;
        fixedField.table = DicVal(fixedField, "table", "");
        fixedField.logpid = G_id.BASE;
        fixedField.type = "100";
        fixedField.datatype = CELLTYPE.SPIN;
        fixedField.options = [{ code: "0", name: "自動" }, { code: "1", name: "手動" }];
        fixedField.hidden = "2";
        if (isEmpty(fixedField.length)) fixedField.length = 0;
        if (isEmpty(fixedField.format)) fixedField.format = "";
        if (isEmpty(fixedField.decimalplace)) fixedField.decimalplace = 0;
        if (isEmpty(fixedField.dbcolumn)) fixedField.dbcolumn = "";
        if (isEmpty(fixedField.dataid)) fixedField.dataid = "";
        if (isEmpty(fixedField.initvalue)) fixedField.initvalue = "0";
        if (isEmpty(fixedField.trigger)) fixedField.trigger = [];
        fixedField.required = !!fixedField.required;
        fixedField.locked = true;
        fixedField.unchange = true;
        fixedField.primary = !!fixedField.primary;
        fixedField.multiple = !!fixedField.multiple;
        fixedField.history = !!fixedField.history;
        return fixedField;
    }
    function resolveFixedHinbanNumberingField(basefields) {
        if (isEmpty(basefields)) return createFixedHinbanNumberingField();
        const exact = basefields.find(e => isFixedHinbanNumberingTarget(e) && e.columnnm === FIXED_HINBAN_NUMBERING_LABEL);
        if (!isEmpty(exact)) return normalizeFixedHinbanNumberingField(exact);
        return createFixedHinbanNumberingField();
    }
    function insertFixedHinbanNumberingField(fields, fixedField) {
        if (isEmpty(fields)) return isEmpty(fixedField) ? [] : [fixedField];
        const normalizedFixed = normalizeFixedHinbanNumberingField(fixedField);
        const newfields = fields
            .filter(e => !(String(DicVal(e, "type", "")) === "100" && (e.columnid === normalizedFixed.columnid || e.columnnm === FIXED_HINBAN_NUMBERING_LABEL)))
            .map(e => DeepCopy(e));
        const targetIndexes = [];
        for (let i = 0; i < newfields.length; i++) {
            if (isFixedHinbanNumberingTarget(newfields[i])) targetIndexes.push(i);
        }
        if (targetIndexes.length === 0) {
            newfields.push(normalizedFixed);
            return newfields;
        }
        const insertIndex = targetIndexes.length >= 8 ? targetIndexes[7] : targetIndexes[targetIndexes.length - 1] + 1;
        newfields.splice(insertIndex, 0, normalizedFixed);
        return newfields;
    }

    return {
        calculateCostRateValue,
        createCostRateField,
        createSubBrandField,
        normalizeSubBrandField,
        resolveSubBrandField,
        insertSubBrandField,
        normalizeCostRateField,
        resolveCostRateField,
        insertCostRateField,
        applyCostRateToEntryData,
        createBrandHinbanField,
        normalizeBrandHinbanField,
        resolveBrandHinbanField,
        insertBrandHinbanField,
        buildHinbanHeadlineColumnInfo,
        formatHinbanHeadlineValue,
        resolveBrandHinbanPrefix,
        normalizeBrandHinbanSerialValue,
        buildBrandHinbanCode,
        createBrandHinbanSerialSearchField,
        handleBrandHinbanSerialSelection,
        hasBrandHinbanPrerequisites,
        applyBrandHinbanCodeToEntryData,
        normalizeFixedHinbanNumberingField,
        resolveFixedHinbanNumberingField,
        insertFixedHinbanNumberingField
    };
}
